/**
 * Custom hook for the owner-only salary register.
 * Probes /access first — a 403 means the area is not available to the
 * current user and the section should render nothing.
 *
 * The register auto-populates from Xero Payroll; the only mutation is
 * toggling the billable flag per person (PUT /:id/billable).
 */

import { useState, useEffect, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { apiClient, handleApiError } from '@/api/apiClient';
import type {
  SalaryBillableInput,
  SalaryRegisterResponse,
} from '@/types/salaryRegister';

interface UseSalaryRegisterResult {
  /** null = still probing; false = not allowed; true = allowed */
  allowed: boolean | null;
  data: SalaryRegisterResponse | null;
  loading: boolean;
  error: string | null;
  mutating: boolean;
  mutateError: string | null;
  refresh: () => Promise<void>;
  setBillable: (id: string, billable: boolean) => Promise<boolean>;
}

export function useSalaryRegister(): UseSalaryRegisterResult {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [data, setData] = useState<SalaryRegisterResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ data: SalaryRegisterResponse }>(
        '/salary-register'
      );
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load salary register');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load data'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await apiClient.get('/salary-register/access');
        if (cancelled) return;
        setAllowed(true);
        await fetchData();
      } catch (err) {
        if (cancelled) return;
        // Fail-secure per SECURITY-STANDARDS: still hide the section on
        // any error, but a 403 is the expected "not allowed" signal
        // (render nothing). Any other failure (500, network, middleware
        // crash) is a BROKEN auth gate — surface an error AND log to
        // the console so the user (and operators inspecting the browser
        // console) can distinguish "denied" from "the gate is broken".
        const status =
          isAxiosError(err) && err.response ? err.response.status : null;
        setAllowed(false);
        setLoading(false);
        if (status !== 403) {
          const message =
            'Unable to verify access to the Cost per person area. Try refreshing.';
          setError(message);
          // Alert-level signal: the security gate itself failed to
          // execute, not a legitimate denial. This is the only signal
          // to operators that the auth middleware/proxy is broken.
          // Log status only — never the raw err.message (which could
          // contain backend internals if the server is misconfigured).
          // SEC-VAL-01 (OWASP A05): avoid data exposure via console.
          console.error('[SalaryRegister] auth gate probe failed', {
            status,
            errorType: isAxiosError(err) ? 'axios' : 'unknown',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchData]);

  const refresh = useCallback(async () => {
    if (allowed) await fetchData();
  }, [allowed, fetchData]);

  const mutate = useCallback(
    async (
      fn: () => Promise<unknown>,
      failureMessage: string
    ): Promise<boolean> => {
      try {
        setMutating(true);
        setMutateError(null);
        await fn();
        await fetchData();
        return true;
      } catch (err) {
        try {
          handleApiError(err, failureMessage);
        } catch (handled) {
          setMutateError(
            handled instanceof Error ? handled.message : failureMessage
          );
        }
        return false;
      } finally {
        setMutating(false);
      }
    },
    [fetchData]
  );

  const setBillable = useCallback(
    (id: string, billable: boolean) => {
      const input: SalaryBillableInput = { billable };
      return mutate(
        () => apiClient.put(`/salary-register/${id}/billable`, input),
        'Failed to update billable flag'
      );
    },
    [mutate]
  );

  return {
    allowed,
    data,
    loading,
    error,
    mutating,
    mutateError,
    refresh,
    setBillable,
  };
}
